'use client';

import {
  FC,
  PropsWithChildren,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { mapCtx } from './MapContext';
import { useInvariantCallbackRef } from '../../../hooks';
import { MapProps } from './types';
import { getIsMapHidden } from './utils';
import { Conditional } from '../conditional';

/**
 * If a map id is not provided it's fine to use DEMO_MAP_ID.
 * See https://developers.google.com/maps/documentation/javascript/advanced-markers/migration
 */
const DEMO_MAP_ID = 'DEMO_MAP_ID';

export const Map: FC<PropsWithChildren<MapProps>> = ({
  userCountryCode,
  dealerCode,
  mapOptions = {},
  zoom,
  center,
  bounds,
  onClick,
  onZoomChange,
  onCenterChange,
  onBoundsChange,
  children,
  className
}) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const initialOptionsRef = useRef(mapOptions);
  const onClickRef = useInvariantCallbackRef(onClick);
  const onZoomChangeRef = useInvariantCallbackRef(onZoomChange);
  const onCenterChangeRef = useInvariantCallbackRef(onCenterChange);
  const onBoundsChangeRef = useInvariantCallbackRef(onBoundsChange);

  const isHidingMaps = getIsMapHidden({ userCountryCode, dealerCode });

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    const newMap = new google.maps.Map(node, {
      mapId: DEMO_MAP_ID,
      ...initialOptionsRef.current
    });
    setMap(newMap);

    /**
     * Events Handlers
     */
    const clickListener = newMap.addListener(
      'click',
      (ev: google.maps.MapMouseEvent) => {
        const position = ev.latLng?.toJSON();
        position && onClickRef.current(position);
      }
    );

    const zoomListener = newMap.addListener('zoom_changed', () => {
      const newZoom = newMap.getZoom();
      newZoom !== undefined && onZoomChangeRef.current(newZoom);
    });

    const centerListener = newMap.addListener('center_changed', () => {
      const newCenter = newMap.getCenter()?.toJSON();
      newCenter && onCenterChangeRef.current(newCenter);
    });

    const boundsListener = newMap.addListener('bounds_changed', () => {
      const newBounds = newMap.getBounds()?.toJSON();
      newBounds && onBoundsChangeRef.current(newBounds);
    });

    return () => {
      clickListener?.remove();
      zoomListener?.remove();
      centerListener?.remove();
      boundsListener?.remove();
    };
  }, [onBoundsChangeRef, onCenterChangeRef, onClickRef, onZoomChangeRef]);

  //  these useLayoutEffects must be in the order of dependencies bounds, zoom, and center so that panTo happens after fitBounds
  useLayoutEffect(() => {
    if (map && bounds) {
      /**
       * Prevent set bounds if are the same the map already has
       */
      const mapBounds = map.getBounds();
      const newBounds = new google.maps.LatLngBounds(bounds);
      if (!mapBounds?.equals(newBounds)) map.fitBounds(newBounds);
    }
  }, [map, bounds]);

  useLayoutEffect(() => {
    if (map && typeof zoom === 'number' && zoom !== map.getZoom()) {
      /**
       * Prevent set zoom if is the same the map already has
       */
      map.setZoom(zoom);
    }
  }, [map, zoom]);

  useLayoutEffect(() => {
    if (map && center) {
      /**
       * Prevent set center if is the same the map already has
       */
      const mapCenter = map.getCenter();
      const newCenter = new google.maps.LatLng(center);
      if (!mapCenter?.equals(newCenter)) map.panTo(newCenter);
    }
  }, [map, center]);

  return (
    <Conditional test={!isHidingMaps}>
      <mapCtx.Provider value={map}>
        <div ref={ref} className={`h-100 ${className ?? ''}`} data-testid="map">
          {children}
        </div>
      </mapCtx.Provider>
    </Conditional>
  );
};
